import { getPageId } from "@/services/utl-service"
import { iteratee } from "lodash"

export interface FbLoginToken {
    redirectUrl: string,
    uuid: string
}
export interface FbPage {
    storeId: bigint,
    pageId: string,
    pageName: string,
    pageAccessToken: string,
    susbcribeFields: string,
    connectedBy: string,
    createdAt: string,
    profileUrl: string,

}
export interface RecentMedia {
    url: string,
    type: string
}
export interface FbRecentPost {
    id: string,
    message: string,
    link: string
    mediaList: RecentMedia[]
}

export interface FBPost {
    id: string;
    message?: string | null;
    createdTime: string; // ISO8601 timestamp from Facebook
    permalinkUrl?: string | null;
    statusType?: string | null;
    story?: string | null;
    fromId?: string | null;
    fromName?: string | null;
    shares: number; // default 0 if null
    totalComments: number; // from comments.summary(true)
    totalReactions: number; // from reactions.summary(true)
    mediaType?: string | null; // e.g. "photo", "video", "link"
    mediaUrl?: string | null;
    thumbnailUrl?: string | null;
}



export interface Participant {
    id: string,
    name: string,
    username: string,
    profileImageUrl?: string,
    conversationId?: string,
}

export class FbConversation {
    id!: string;
    /** Page that owns this conversation. Set for cached cross-page order history. */
    pageId?: string;
    cacheOnly?: boolean;
    snippet!: string;
    threadKey!: string;
    updatedTime!: string;
    messageCount!: number;
    unreadCount!: number;
    participants!: Participant[];
    lastCustomerMessageTime?: string | null;
    messagingWindow?: 'STANDARD' | 'HUMAN_AGENT' | 'CLOSED' | 'UNKNOWN';
    enableHumanAgent?: boolean;
    followUpUser?: string | null;
    assignedUsers?: string[];
    isAssignedToUser(username?: string | null): boolean {
        if (!username) return false;
        const target = username.trim().toLowerCase();
        if (!target) return false;
        if (this.followUpUser && this.followUpUser.trim().toLowerCase() === target) {
            return true;
        }
        return (this.assignedUsers ?? []).some((u) => u && u.trim().toLowerCase() === target);
    }
    isOnline(): boolean {
        if (this.updatedTime) {
            const date = new Date(this.updatedTime);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const minutes = Math.floor(diffMs / 60000);
            const MINUTE_THRESHOLD = 24 * 60;
            return minutes < MINUTE_THRESHOLD;
        }
        return false;
    }
    getUser(): Participant | null {
        var currentPageId = getPageId();
        if (currentPageId) {
            for (const p of this.participants) {
                if (p.id != currentPageId) {
                    p.conversationId = this.id;
                    return p;
                }
            }
        }
        return this.participants?.[0] ?? null;
    }
    getChatTime(): string {
        const date = new Date(this.updatedTime);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        const seconds = Math.floor(diffMs / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (seconds < 60) return "just now";
        if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
        if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
        if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

        // Fallback: show date (YYYY/MM/DD)
        return `${date.getFullYear()}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`;
    }
};
export interface ImageData {
    width: number,
    height: number,
    url: string,
    previewUrl: string,
    maxWidth: number,
    maxHeight: number,
    renderAsSticker: boolean,

}
export interface Attachment {
    id: string,
    mimeType: string,
    image: boolean,
    imageData?: ImageData,
    size: number,
    fileUrl: string
    video: boolean
    audio?: boolean
}

export class FBMessage {
    id!: string;
    createdTime!: string;
    from!: Participant;
    to!: Participant[];
    message!: string;
    sticker!: string;
    attachments?: Attachment[];
    getSender(pageIdOverride?: string, customerIdOverride?: string): 'admin' | 'customer' {
        const norm = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
        const pageId = norm(pageIdOverride) || norm(getPageId());
        const customerId = norm(customerIdOverride);
        // `from` may be missing/empty (optimistic or raw websocket payloads) — fall back to raw `sender`
        const fromId = norm(this.from?.id) || norm((this as any).sender?.id);

        // A historical message may have been sent by the Page or by an admin identity whose
        // id is not the Page id. The conversation's customer PSID is therefore the most
        // reliable discriminator: only that sender is the customer.
        if (customerId) {
            if (fromId) return fromId === customerId ? 'customer' : 'admin';

            const toIds = (this.to ?? []).map((p) => norm(p?.id));
            const recipientId = norm((this as any).recipient?.id);
            if (toIds.includes(customerId) || recipientId === customerId) return 'admin';
            if (pageId && (toIds.includes(pageId) || recipientId === pageId)) return 'customer';
        }

        if (pageId) {
            if (fromId) return fromId === pageId ? 'admin' : 'customer';
            // No sender id: if the page is the recipient, the customer sent it
            const toIds = (this.to ?? []).map((p) => norm(p?.id));
            if (toIds.includes(pageId)) return 'customer';
            const recipientId = norm((this as any).recipient?.id);
            if (recipientId === pageId) return 'customer';
            return 'admin';
        }
        return 'customer';
    }
    getCreatedAt(): Date {
        const date = new Date(this.createdTime);
        return date;

    }

}

export interface PageResult<T> {
    data: T[];
    nextCursor: string | null;
    prevCursor: string | null;
};
export interface FBLive {
    id: string;
    postId: string;
    title: string | null;
    status: string;
    liveViews: number | null;
    creationTime: string;
    broadcastStartTime: string | null;
    permalinkUrl: string;
    streamUrl: string | null;
    secureStreamUrl: string | null;
    commentCount: number;
    reactionCount: number;
    previewUrl?: string | null;
    description?: string | null;
}

export interface FacebookComment {
    id: string;
    fromId: string;
    fromName: string;
    message: string;
    createdTime: string;
    profileImage: string;
    taggedProduct: boolean;
    replyCount: number; //todo
    isPinned: boolean;
    isNew: boolean;
    postId?: string;
    orderByComment?: OrderByComment | null;


}
export interface FetchCommentRespoonse {
    lastFetchedAt: string;
    comments: FacebookComment[];
}

/** Supporting types — simplified placeholders */
export interface FacebookUser {
    id: string;
    name: string;
    picture?: { data: { url: string } };
}

export interface Likes {
    data?: FacebookUser[];
    summary?: { totalCount?: number };
}

export interface Reactions {
    data?: Array<{ id: string; type: string; name: string }>;
    summary?: { totalCount?: number };
}

export interface Conversation {
    id: string;
    link?: string;
}

export interface NamedFacebookType {
    id: string;
    name?: string;
}

export interface Comments {
    data: FacebookComment[];
    paging?: {
        cursors?: { before?: string; after?: string };
        next?: string;
        previous?: string;
    };
}
export interface AutoReplyMessage {
    pageId: string;
    pageUserId: string;
    content: string;
    id: string;
    timestamp: string;          // ISO string from LocalDateTime
    facebookUser: Participant; // your nested object
}
export enum QueueError {
    NeedReplyMsg = "NeedReplyMsg",
    ErrorReply = "ErrorReply",
}
export type QueueCustomer = {
    id: string,
    customerId: string;              // Long -> number
    name: string;
    profileImageUrl?: string | null; // nullable-safe
    lastItem?: string | null;
    referenceId?: string | null;
    lastReferenceType?: string | null;
    lastContent?: string | null;
    state: number;                   // default = 1 on backend
    queueError?: QueueError | null;
    createdAt: string;
    updatedAt: string;
    permalinkUrl: string | null;
    lastComment?: string | null;

};

export interface QueueUser {
    id: string;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
    userId: string | null;
    email: string | null;
    username: string | null;
    name: string | null;
    metadata: any | null;
    type: string | null;
    conversationId: string | null;
}
export interface AutoReply {
    pageId: string;
    pageUserId: string;
    content: string;
    id: string;
    timestamp: string;        // ISO format from LocalDateTime
    facebookUser: FbAutoReplyUser;
}
export interface FbAutoReplyUser {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    profileImageUrl?: string | null;
    email?: string | null;
    username?: string | null;
    name?: string | null;
    userId?: string | null;
    metadata?: any | null;
    type?: string | null;
}
export enum OrderByCommentDetectState {
    SUCCESS = "SUCCESS",
    NOT_DETECT = "NOT_DETECT",
    MANUAL = "MANUAL",
    DETECT_ERROR = "DETECT_ERROR"
}
export interface OrderByComment {
    commentId: string;
    postId: string;
    saleCode: string;
    orderId: string; // Long is represented as number in TS
    orderNumber: string;
    totalAmount: number; // BigDecimal is usually handled as number
    totalQty: number;
    qty: number;
    price: number;
    timestamp: string;
    errorMsg: string;
    customerBlocked?: boolean;
    customerName?: string;
    detectState: OrderByCommentDetectState;
}
